import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { SIDE_PANEL_WIDTH } from "_shared/_views/side-panel-width";
import CommentAudienceSwitch from "work/proposals/comments/_view/comment-audience-switch";
import CommentComposer from "work/proposals/comments/_view/comment-composer";
import CommentItem from "work/proposals/comments/_view/comment-item";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalTimelinePanel from "work/proposals/negotiation/proposal-timeline-panel";
import Proposal from "work/proposals/proposal";

const Drawer = styled(MuiDrawer)(() => ({
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    marginTop: '64px',
    width: SIDE_PANEL_WIDTH,
    boxSizing: 'border-box',
  },
}));
const CommentsPanel = styled(Drawer, { shouldForwardProp: (prop) => prop !== "closeAfterTransition" })(() => ({
}));
const CommentsPanelContainer = styled("div")(() => ({
  flex: 1,
  height: "100%",
}));
// Fills the paper rather than restating its width: the two used to be the same 400px literal, which
// added the padding on top and pushed 32px of every comment past the panel's edge. A panel that
// narrows with the window would have made that a clipped composer instead of an invisible overflow.
const CommentsPanelContent = styled("div")(({ theme }) => ({
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  padding: theme.spacing(2),
  width: "100%",
}));
const TitleBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  flexDirection: "row",
  paddingBottom: theme.spacing(1),
}));
const HistorySection = styled("div")(({ theme }) => ({
  overflowY: "auto",
  paddingTop: theme.spacing(1),
}));
const ChangesUnavailable = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));
// The negotiation this revision continues, below the one it is. Separated by a rule and dimmed a
// little rather than folded into the stream above: they are two negotiations, and a comment written
// about the terms being revised is not a comment on the revision.
const CarriedOverSection = styled("div")(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  marginTop: theme.spacing(2),
  opacity: 0.85,
  paddingTop: theme.spacing(1),
}));
const CarriedOverHeading = styled(Typography)(({ theme }) => ({
  paddingBottom: theme.spacing(0.5),
}));
// A comment in the stream carries the same colored left rule as the change entries beside it, so one
// list reads as one list.
const StreamedComment = styled(CommentItem)(({ theme }) => ({
  borderLeft: `3px solid ${theme.palette.primary.main}`,
  paddingLeft: theme.spacing(1.25),
}));

/** What the pane calls itself, now that comments and changes are one stream rather than three tabs. */
export const proposalCommentsAndChangesTitle = "Comments/Changes";

type CommentsDrawerProps = {
  proposal: Proposal | null | undefined;
  // The comment layer reads/writes Messaging Forums keyed by the durable negotiationId. The page
  // supplies a comment-scoped proposal (id = negotiationId) plus its populated threads.
  commentThreads: CommentThread[] | undefined;
  isOpen: boolean;
  isSaving?: boolean;
  readOnly?: boolean;
  showMarkAllRead?: boolean;
  title?: string;
  negotiationId?: string;
  viewerScope?: string;
  /**
   * The negotiation this one revises, when it revises one: the stream the agreed terms were settled
   * in, and the forums its comments are filed under.
   *
   * A revision of hired work opens on terms both parties already agreed, and everything said while
   * they were agreed is the context for changing them. Reading it meant leaving the revision, finding
   * the archived proposal and reading it there. Absent for a first-time offer, and absent for a
   * reader the prior negotiation does not admit.
   */
  carriedOverNegotiationId?: string;
  carriedOverViewerScope?: string;
  onOpen: () => void;
  onClose: () => void;
};

/**
 * The proposal's Comments/Changes pane.
 *
 * It used to carry three tabs — Changes, Comments, All — and open on whichever one the caller thought
 * explained why it had opened. There is one thing to show now: the negotiation's comments zipped
 * together with its change history, ordered by time. The tabs went with the choice; so did the
 * field-name heading, because neither half of the stream belongs to a field any more.
 */
export default function CommentsDrawer(props: Readonly<CommentsDrawerProps>) {
  const {
    proposal,
    commentThreads,
    isOpen,
    title,
    negotiationId,
    viewerScope,
    carriedOverNegotiationId,
    carriedOverViewerScope,
    onClose,
  } = props;
  const isSaving = props.isSaving ?? false;
  const readOnly = props.readOnly ?? false;
  const showMarkAllRead = props.showMarkAllRead ?? true;

  return (
    <CommentsPanel open={isOpen} anchor="right" variant="persistent" closeAfterTransition={false}>
      <CommentsPanelContainer>
        <CommentsPanelContent>
          <TitleBar>
            <Typography variant="h5">{title || proposalCommentsAndChangesTitle}</Typography>
            <Tooltip title="Minimize comments panel">
              <IconButton size="medium" onClick={onClose}>
                <KeyboardTabIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </TitleBar>
          {proposal?.id && (
            <>
              <CommentComposer
                proposal={proposal}
                commentThreads={commentThreads ?? []}
                isSaving={isSaving}
                readOnly={readOnly}
                showMarkAllRead={showMarkAllRead}
              />
              {negotiationId && viewerScope ? (
                <HistorySection data-testid="comments-and-changes-history">
                  {/* One stream: the Work field-change timeline interleaved with the Messaging comments,
                      ordered by timestamp. Comments do not ride the Work stream, so the panel folds them
                      in from the Forum/Message comment store.

                      A comment renders as the comment, not as a line of history about one. This is the
                      only surface a live negotiation's comments are read on, so it is the only place
                      their author can edit, retract, delete or re-aim them and a recipient can mark one
                      unread — drawn without that, the merge quietly took every one of those away. */}
                  <ProposalTimelinePanel
                    negotiationId={negotiationId}
                    viewerScope={viewerScope}
                    // The pane is persistent: its content stays mounted while it is minimized. Asking for
                    // the negotiation's history on behalf of a pane nobody is looking at costs a request
                    // per proposal page, and it outlives the actor's access — a reviewer removed from the
                    // proposal had their closed pane still asking for a timeline they may no longer read.
                    enabled={isOpen}
                    includeComments
                    renderComment={(comment) => (
                      <StreamedComment
                        comment={comment}
                        readOnly={readOnly}
                        renderAudienceSwitch={
                          readOnly
                            ? undefined
                            : (audience) => <CommentAudienceSwitch proposal={proposal} {...audience} />
                        }
                      />
                    )}
                  />
                  {carriedOverNegotiationId && carriedOverViewerScope && (
                    <CarriedOverSection data-testid="carried-over-negotiation">
                      <CarriedOverHeading variant="subtitle2" color="text.secondary">
                        Before this revision
                      </CarriedOverHeading>
                      {/* The same panel, pointed at the negotiation that produced the agreement being
                          revised. Read-only throughout: those comments belong to a negotiation that is
                          over, and their authors cannot edit, retract or re-aim them from here. */}
                      <ProposalTimelinePanel
                        negotiationId={carriedOverNegotiationId}
                        viewerScope={carriedOverViewerScope}
                        enabled={isOpen}
                        includeComments
                        renderComment={(comment) => <StreamedComment comment={comment} readOnly />}
                      />
                    </CarriedOverSection>
                  )}
                </HistorySection>
              ) : (
                <ChangesUnavailable>
                  <Typography variant="body2" color="text.secondary">
                    Change history is unavailable until the proposal negotiation finishes loading.
                  </Typography>
                </ChangesUnavailable>
              )}
            </>
          )}
        </CommentsPanelContent>
      </CommentsPanelContainer>
    </CommentsPanel>
  );
}
